<template>
  <div
    class="ls-schedule-service-item"
    :class="{
      'border-top': dividerTop,
      'border-bottom': dividerBottom,
    }"
    @click="onClick"
    @mouseover="hover = true"
    @mouseleave="hover = false"
  >
    <LsServiceEventItemHeader
      :service-name="getScheduleServiceName"
      :service-frequency="getFrequency"
      :service-scope="getScope"
      :enable-quick-actions="enableQuickActions"
      :schedule="schedule"
      :hover="hover"
    />
    <LsServiceEventItem
      :icon="getInfo.icon"
      :status-text="getInfo.statusText"
      :date-override-text="getInfo.pending"
      :start-date="getInfo.startDate"
      :end-date="getInfo.endDate"
      :is-mowing="isMowing"
      :is-ltrs-provided-by-partner="isLtrsProvidedByPartner"
      :is-ltrs-service-window-confirmed="isLtrsServiceWindowConfirmed"
      :schedule="schedule"
    />
    <div
      v-if="getOTSDC"
      class="otsdc"
      test-id="otsdc"
      @mouseover="hoverOTSDC = true"
      @mouseout="hoverOTSDC = false"
      @click.stop="openModal"
    >
      <div class="otsdc-icon">!</div>
      <span :class="{ 'otsdc-label': hoverOTSDC }">One Time Reschedule - {{ getOTSDC }}</span>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import VueTypes from 'vue-types';
import {
  lsFilters,
  lsScheduleHelpers,
  lsPropTypeHelpers,
  lsTrackingService,
  lsPropertyHelpers,
  lsModalService,
} from 'ls-vue-common';
import LsServiceEventItem from '../LsServiceEventItem/LsServiceEventItem.vue';
import { lsScheduleServiceItemHelpers } from './LsScheduleServiceItemHelpers';
import LsServiceEventItemHeader from '@/components/ServiceItems/LsServiceEventItemHeader/LsServiceEventItemHeader.vue';
import { OneTimeRescheduleModal } from '@/modals';
import { INSTANT_QUOTE_STATUS } from '@/constants';
import { lsInstantQuoteFormHelpers } from '@/helpers';

export default {
  name: 'LsScheduleServiceItem',
  components: {
    LsServiceEventItem,
    LsServiceEventItemHeader,
  },
  props: {
    schedule: VueTypes.shape({
      blockingRequiredServices: VueTypes.array,
      comments: VueTypes.shape({}),
      contractor: VueTypes.shape({}).loose,
      crew: VueTypes.shape({}).loose,
      created_at: VueTypes.string,
      cycle: VueTypes.number,
      deleted_at: VueTypes.custom(lsPropTypeHelpers.stringOrNull),
      end_date: VueTypes.custom(lsPropTypeHelpers.stringOrNull),
      is_paused: VueTypes.bool,
      isLtrsProvidedByPartner: VueTypes.bool,
      isLtrsServiceWindowConfirmed: VueTypes.bool,
      key: VueTypes.string,
      manualquote: VueTypes.shape({}).loose,
      next_ends_at: VueTypes.string, // YYYY-MM-DD
      next_eventstatus: VueTypes.string,
      next_in_paused_season: VueTypes.bool,
      next_scheduleevent_id: VueTypes.number,
      next_starts_at: VueTypes.string, // YYYY-MM-DD
      pause_can_resume: VueTypes.bool,
      paw: VueTypes.shape({}).loose,
      price: VueTypes.number,
      property: VueTypes.shape({}).loose,
      property_id: VueTypes.number,
      instant_quote: VueTypes.shape({}).loose,
      qualityassurance: VueTypes.shape({
        created_at: VueTypes.string, // YYYY-MM-DD hh:mm:ss,
        description: VueTypes.string,
        qualityassurance_id: VueTypes.number,
        service: VueTypes.shape({
          description: VueTypes.string,
          is_linkable_with_mowing: VueTypes.bool,
          completion_photos_mandatory: VueTypes.bool,
          can_skip: VueTypes.bool,
          is_ltrs: VueTypes.bool,
          name: VueTypes.string,
          service_id: VueTypes.number,
          str_id: VueTypes.string,
        }).loose,
        updated_at: VueTypes.string, // YYYY-MM-DD hh:mm:ss,
      }).loose,
      quote_schedule_id: VueTypes.custom(lsPropTypeHelpers.numberOrNull),
      scheduleevents: VueTypes.array,
      schedule_id: VueTypes.number,
      service: VueTypes.shape({
        description: VueTypes.string,
        is_linkable_with_mowing: VueTypes.bool,
        is_ltrs: VueTypes.bool,
        completion_photos_mandatory: VueTypes.bool,
        can_skip: VueTypes.bool,
        name: VueTypes.string,
        service_id: VueTypes.number,
        str_id: VueTypes.string,
      }).loose,
      start_date: VueTypes.string, // YYYY-MM-DD
      status: VueTypes.oneOf([
        'pending',
        'active',
        'cancelled',
        'ended',
        'overdue',
        'new',
        'ready',
        'priced',
        'rejected',
        'expired',
        'authorized',
        'fallback',
      ]),
      status_set_at: VueTypes.string, // YYYY-MM-DD hh:mm:ss
      type: VueTypes.oneOf(['paw', 'qualityassurance', 'manualquote', 'service', 'instant_quote']),
      upcoming_one_time_service_day_changes: VueTypes.arrayOf(
        VueTypes.shape({ status_details: VueTypes.string }).loose
      ),
      updated_at: VueTypes.string, // YYYY-MM-DD hh:mm:ss
    }).isRequired.loose,
    property: VueTypes.shape({}).loose.def(_.constant({})),
    trackId: VueTypes.custom(lsPropTypeHelpers.trackID).isRequired,
    hideServiceName: VueTypes.bool.def(false),
    dividerTop: VueTypes.bool.def(false),
    dividerBottom: VueTypes.bool.def(false),
    displayOnly: VueTypes.bool.def(false),
  },
  data() {
    return {
      hover: false,
      hoverOTSDC: false,
    };
  },
  computed: {
    isLtrsProvidedByPartner() {
      return this.schedule.isLtrsProvidedByPartner;
    },
    isLtrsServiceWindowConfirmed() {
      return this.schedule.isLtrsServiceWindowConfirmed;
    },
    isMowing() {
      return _.get(this.schedule, 'service.str_id') === 'mowing';
    },
    enableQuickActions() {
      return this.isMowing ? this.schedule.status === 'active' : true;
    },
    getScheduleServiceName() {
      if (this.hideServiceName) {
        return null;
      }
      return lsScheduleHelpers.getScheduleServiceName({ schedule: this.schedule });
    },
    getFrequency() {
      if (this.schedule.isLtrsProvidedByPartner) {
        return 'lawnTreatment.recurringCycle';
      }
      const isServiceOrInstantQuote =
        _.get(this.schedule, 'type') === 'service' ||
        _.get(this.schedule, 'type') === 'instant_quote';
      return isServiceOrInstantQuote
        ? `(${lsFilters.frequency({ cycle: this.schedule.cycle })})`
        : null;
    },
    getScope() {
      return this.isMowing ? lsPropertyHelpers.getScope(this.property) : null;
    },
    hasPendingLGQ() {
      const { instantQuotes } = this.property;
      const { next_scheduleevent_id: scheduleEventId } = this.schedule;

      const longGrassQuoteStatus =
        _.get(
          lsInstantQuoteFormHelpers.filterInstantQuoteLongGrassByScheduleEventId({
            instantQuotes,
            scheduleEventId,
          }),
          'instant_quote_status'
        ) || '';

      return longGrassQuoteStatus === INSTANT_QUOTE_STATUS.CUSTOMER_PENDING;
    },
    getInfo() {
      if (this.hasPendingLGQ) {
        return {
          icon: { name: 'calendar', color: 'primary' },
          statusText: this.t('upcomingService'),
          pending: this.t('pendingLongGrass'),
        };
      }

      const {
        icon,
        statusText,
        startDate,
        endDate,
      } = lsScheduleServiceItemHelpers.getScheduleServiceItemDisplayValues({
        schedule: this.schedule,
      });

      return { icon, statusText, startDate, endDate };
    },
    getOTSDC() {
      const { upcoming_one_time_service_day_changes } = this.schedule;
      if (_.size(upcoming_one_time_service_day_changes)) {
        return upcoming_one_time_service_day_changes[0].status_details;
      }

      return null;
    },
  },
  methods: {
    onClick() {
      lsTrackingService.track({ event: 'click', target: this.trackId });
      this.$emit('click');
    },
    openModal() {
      lsModalService.open({
        parent: this,
        component: OneTimeRescheduleModal,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ls-schedule-service-item {
  cursor: pointer;
  padding: 20px 0;

  &.disable-pointer {
    cursor: initial;
  }
}

.border-bottom {
  border-bottom: 1px solid $primary-light;
}

.border-top {
  border-top: 1px solid $primary-light;
}

.otsdc {
  display: flex;
  flex-direction: 'row';
  margin-top: 5px;

  .otsdc-icon {
    background: $tertiary;
    width: 16px;
    height: 16px;
    font-weight: bold;
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: $size-7;
    color: $white;
    margin-right: 5px;
  }

  span {
    font-weight: bold;
    font-size: $size-6;
    color: $gray-primary;
  }

  .otsdc-label {
    color: $gray-secondary;
  }
}
.themed-app {
  .ls-schedule-service-item {
    .border-bottom {
      border-color: $theme-color-primary-light;
    }
    .border-top {
      border-color: $theme-color-primary-light;
    }
    .otsdc-icon {
      background: $theme-color-warning;
      font-size: var(--theme-txt-help);
      color: $theme-color-white;
    }
    span {
      font-size: $size-6;
      color: $theme-color-gray-darker;
    }
    .otsdc-label {
      color: $theme-color-gray-dark;
    }
  }
}
</style>
